import { FeatureCollection } from 'geojson';
import { stringify } from 'query-string';
import { useMutation, UseMutationResult, useQuery } from 'react-query';
import { useAuth } from '../auth/AuthProvider';
import { StoryConfig } from '../shared/components/storytelling/types/data';
import {
    FeatureCollectionAcquisitions,
    Insights,
    MetaDataFeatureCollection,
    PathfinderData,
    PathfinderFiles,
} from '../utils/interfaces';
import { getRequestHeaders, vake_api_server_url } from './helpers';
import { queryKeyUseInsightsPerDateQuery, queryKeyUseInsightsPerDateQueryMinimal } from './query-keys';

export enum SubscriptionState {
    success,
    failed,
    unsent,
}

// Vake-api

export const useMetadataQuery = (reportUUID?: string, searchParams?: unknown) => {
    const { token } = useAuth();

    let url = `${vake_api_server_url}/report/overwatch/${reportUUID}/metadata`;

    // TODO: Make this prettier. Check if aoi is in SearchParams
    if (searchParams && Object.values(searchParams).length > 0) {
        url = `${url}?${stringify({ aoi: searchParams.aoi })}`;
    }

    return useQuery<MetaDataFeatureCollection>(
        ['metadata', reportUUID],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const metaData = await res.json();
            return await metaData;
        },
        {
            staleTime: Infinity,
            enabled: !!reportUUID,
        }
    );
};

export const useInsightsPerDateQuery = (
    reportUUID: string | undefined,
    date: Date | undefined,
    processing_status: boolean,
    searchParams?: unknown
) => {
    const { token } = useAuth();
    return useQuery<Insights>(
        [queryKeyUseInsightsPerDateQuery, reportUUID, date, processing_status],
        async () => {
            let url = `${vake_api_server_url}/report/overwatch/${reportUUID}/detections/date?`;

            url += new URLSearchParams({
                ...searchParams,
                date: date.toISOString().split('T')[0],
                processing_status: processing_status,
            }).toString();

            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            enabled: !!date && !!reportUUID,
            staleTime: Infinity,
            cacheTime: 1000 * 60 * 60,
        }
    );
};
export const useGetObjects = (reportUUID: string | undefined, date: Date | null, objectLabel: ObjectLabel) => {
    const { token } = useAuth();
    return useQuery<FeatureCollection>(
        [queryKeyUseInsightsPerDateQueryMinimal, reportUUID, date, objectLabel],
        async () => {
            let url = `${vake_api_server_url}/report/overwatch/${reportUUID}/detections/date/minimal?`;
            url += new URLSearchParams({
                report_date: date ? date.toISOString().split('T')[0] : '',
                object_label: objectLabel,
            }).toString();
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        {
            enabled: !!date && !!reportUUID,
        }
    );
};
export const useGetFootprints = (
    reportUUID: string | undefined,
    date: Date | null,
    processingStatus: boolean,
    imageSource: ImageSource,
    sourceActive: boolean
) => {
    const { token } = useAuth();
    return useQuery<FeatureCollection>(
        ['useGetFootprints', reportUUID, date, processingStatus, imageSource],
        async () => {
            const url = `${vake_api_server_url}/report/overwatch/${reportUUID}/detections/date/footprints`;
            const params = new URLSearchParams({
                report_date: date ? date.toISOString().split('T')[0] : '',
                processing_status: processingStatus.toString(),
                image_source: imageSource,
            });
            const finalUrl = `${url}?${params.toString()}`;
            const res = await fetch(finalUrl, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        {
            enabled: !!date && !!reportUUID && sourceActive,
        }
    );
};

export const useAvailableDatesQuery = (reportUUID?: string, searchParams?: unknown) => {
    const { token } = useAuth();
    const init_data: Date[] = [];
    let url = `${vake_api_server_url}/report/overwatch/${reportUUID}/dates`;

    // TODO: Make this prettier. Check if aoi and dates is in SearchParams
    url = searchParams && Object.values(searchParams).length > 0 ? `${url}?${stringify(searchParams)}` : url;

    return useQuery<Date[]>(
        ['available_dates', reportUUID],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const data = await res.json();
            const dates: Date[] = data.map((date: string) => new Date(date));
            return dates;
        },
        {
            placeholderData: init_data,
            enabled: !!reportUUID,
            staleTime: 1000 * 60 * 60, // one hour
        }
    );
};

export const useFutureAcqusitionDatesQuery = (params: unknown, futureDates: number) => {
    const id = params.id ? params.id : 'demo';
    const { token } = useAuth();
    const init_data: Date[] = [];
    const url = `${vake_api_server_url}/report/overwatch/${id}/future_dates?n_future_days=${futureDates}`;
    return useQuery<Date[]>(
        ['future_acq', id],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const data = await res.json();
            const dates: Date[] = data.map((dateResponse: { date: string }) => new Date(dateResponse.date));
            return dates;
        },
        {
            placeholderData: init_data,
            staleTime: 1000 * 60 * 60, // one hour
        }
    );
};

export interface PFMetadata {
    reportName: string | null;
    imo: string | null;
}

export const usePFMetadataQuery = (bucketID: string | null, mmsi: string | null) => {
    const { token } = useAuth();

    const url = `${vake_api_server_url}/report/pathfinder/${bucketID}/metadata?mmsi=${mmsi}`;
    return useQuery<PFMetadata>(
        ['metadata', bucketID],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const metaData = await res.json();
            return await metaData;
        },
        {
            staleTime: Infinity,
            enabled: bucketID !== null && mmsi !== null,
        }
    );
};

export const usePathfinderData = (params: unknown) => {
    const id = params.id;
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/pathfinder/${id}/data`;
    return useQuery<PathfinderData>(
        ['pathfinder_data', id],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const sendSlackBotReportMessage = async (
    token: string | null | undefined,
    currUrl: string,
    hasImages: boolean | null,
    hasAIS: boolean | null
) => {
    const url = `${vake_api_server_url}/user-feedback/`;
    const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: getRequestHeaders(token),
        body: JSON.stringify({ current_url: currUrl, has_images: hasImages, has_ais: hasAIS }),
    });
    if (!response.ok) {
        throw new Error('slack bot troubles');
    }
    return response.ok;
};

export const updateSlackBotMessage = async (
    token: string | null | undefined,
    currUrl: string,
    hasImages: boolean | null,
    hasAIS: boolean | null
) => {
    const url = `${vake_api_server_url}/user-feedback/update_message`;
    const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: getRequestHeaders(token),
        body: JSON.stringify({ current_url: currUrl, has_images: hasImages, has_ais: hasAIS }),
    });
    if (!response.ok) {
        throw new Error('slack bot troubles');
    }
    return response.ok;
};

export const usePathfinderFilesExists = (params: unknown) => {
    const id = params.id;
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/pathfinder/${id}/check_files_exists`;
    return useQuery<PathfinderFiles>(
        ['pathfinder_files_exists', id],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const usePathfinderAisData = (params: unknown) => {
    const id = params.id;
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/pathfinder/${id}/ais_data`;
    return useQuery<FeatureCollection>(
        ['pathfinder_ais_data', id],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const useStoryData = (params: unknown) => {
    const id = params.id;
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/story/${id}/data`;
    return useQuery<StoryConfig>(
        ['story_data'],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const useCloudsPerDateQuery = (reportUUID: string | undefined, date: Date, searchParams: unknown) => {
    const { token } = useAuth();
    let url = `${vake_api_server_url}/report/overwatch/${reportUUID}/clouds`;
    // TODO: Make this prettier. Check if aoi is in SearchParams
    return useQuery<FeatureCollection>(
        ['cloudmasks', date],
        async () => {
            url =
                searchParams && Object.values(searchParams).length > 0
                    ? `${url}?${stringify({ date: date.toISOString().split('T')[0], aoi: searchParams.aoi })}`
                    : `${url}?${stringify({ date: date.toISOString().split('T')[0] })}`;
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const output = await res.json();
            return await output;
        },
        {
            enabled: !!date && !!reportUUID,
            staleTime: Infinity,
        }
    );
};

export const useCustomDataQuery = (params: unknown) => {
    const id = params.id ? params.id : 'demo';
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/overwatch/${id}/custom_data`;
    // TODO: Make this prettier. Check if aoi is in SearchParams
    return useQuery<FeatureCollection>(
        ['customData'],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const output = await res.json();
            return await output;
        },
        {
            staleTime: Infinity,
        }
    );
};

export const useAcqusitions = (uuid: string | undefined, activeDate: Date | undefined) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/acqusition/future_acqusitions?ow_uuid=${uuid}&active_date=${activeDate?.toISOString()}`;
    const now = new Date().getTime();
    const activeTime = activeDate ? activeDate.getTime() : 0;
    const hours = (now - activeTime) / (60 * 60 * 1000);
    return useQuery<FeatureCollectionAcquisitions>(
        ['future acqusitions', uuid, activeDate],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        {
            staleTime: Infinity,
            enabled: !!uuid && hours < 40,
        }
    );
};

export const useTimeAtPointInAcqusition = (timestamp: Date, lon: number, lat: number) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/acqusition/time_at_point_in_acqusition?date=${timestamp.toISOString()}&lon=${lon}&lat=${lat}`;
    return useQuery<Date | undefined>(
        ['timeAtPoint', lon, lat, timestamp],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const output = await res.json();
            if (output.timeAtPoint && typeof output.timeAtPoint === 'string') {
                const dateTime = new Date(output.timeAtPoint);
                return dateTime;
            }
            return undefined;
        },
        {
            staleTime: Infinity,
        }
    );
};

const fetchDataAsFile = async (url: string, accessToken: string | undefined | null, filename?: string) => {
    try {
        fetch(url, {
            method: 'GET',
            headers: getRequestHeaders(accessToken),
        })
            .then((response) => {
                const disposition = response.headers.get('Content-Disposition');
                if (disposition) {
                    const match = disposition.match(/filename=(.+)/);
                    if (match) {
                        filename = match[1];
                    }
                }

                return response.blob();
            })
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename || 'download.csv';
                document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
                a.click();
                a.remove();
                return blob;
            });
    } catch (error) {
        console.error('Fetch failed:', error);
        throw error;
    }
};

export const downloadCsv = async (reportname: string, params: unknown, date: Date) => {
    // Replaced by useDownloadOverwatchReport with Overwatch 2.0 which uses useQuery instead
    const id = params.id ? params.id : 'demo';
    const url = `${vake_api_server_url}/report/overwatch/${id}/detections/csv?date=${date.toISOString().split('T')[0]}`;
    const accessToken = localStorage.getItem('token')?.replaceAll('"', '');
    fetchDataAsFile(url, accessToken, reportname);
};

export const downloadFile = async (url: string, token: string) => {
    let filename: string | null = null;
    const res = await fetch(url, {
        credentials: 'include',
        headers: getRequestHeaders(token),
    });
    if (!res.ok) {
        try {
            const errorMessage = await res.json();
            throw new Error(errorMessage.detail);
        } catch {
            throw new Error(res.statusText);
        }
    }
    const disposition = res.headers.get('Content-Disposition');
    if (disposition) {
        const match = disposition.match(/filename=(.+)/);
        if (match) {
            filename = match[1];
        }
    }
    const blob = await res.blob();

    const new_url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = new_url;
    a.download = filename || 'download.csv';
    document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
    a.click();
    a.remove();
    return blob;
};
export const useDownloadOverwatchReport = (reportUUID: string, date: Date, download: boolean) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/overwatch/${reportUUID}/detections/csv?date=${
        date.toISOString().split('T')[0]
    }`;
    return useQuery<Blob, Error>(
        ['useDownloadOverwatchReport', reportUUID, date],
        async () => downloadFile(url, token || ''),
        { enabled: download }
    );
};

export const useValidateReportId = (params: unknown, reportTypeUrlString: string, validationNeeded: boolean) => {
    const id = params.id;
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/${reportTypeUrlString}/${id}`;
    const queryId = `validate_${reportTypeUrlString}_id`;

    return useQuery<boolean>(
        [queryId, id],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                return false;
            }

            return true;
        },
        {
            enabled: validationNeeded,
        }
    );
};

export const useIsAdmin = (token: string | null | undefined) => {
    const url = `${vake_api_server_url}/report/admin`;

    return useQuery<boolean>(
        ['admin', null],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (res.status === 200) {
                return true;
            } else {
                return false;
            }
        },
        {
            enabled: !!token,
            staleTime: 1000 * 60 * 60, // one hour
            placeholderData: false,
        }
    );
};

export const useIsUser = (token: string | null | undefined) => {
    const url = `${vake_api_server_url}/report/user`;

    return useQuery<boolean>(
        ['user', null],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (res.status === 200) {
                return true;
            } else {
                return false;
            }
        },
        {
            enabled: !!token,
            staleTime: 1000 * 60 * 60, // one hour
            placeholderData: false,
        }
    );
};

export const useGetCustomerGroup = () => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/user_group`;
    return useQuery<string>(
        ['customerGroup'],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.text();
        },
        {
            enabled: !!token,
            staleTime: 1000 * 60 * 60, // one hour
        }
    );
};

export const orderPathfinderSubscription = async (
    mmsi: string,
    fromTimestamp: Date,
    toTimestamp: Date | null,
    reportName: string,
    token: string | null | undefined,
    callback: (orderSubscriptionSuccess: boolean, reportPath: string | undefined) => void
) => {
    const body = { mmsi: mmsi, start_time: fromTimestamp, end_time: toTimestamp, report_name: reportName };

    const res = await fetch(`${vake_api_server_url}/report/pathfinder/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: getRequestHeaders(token),
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        callback(false, undefined);
    }

    if (res.status === 200) {
        const output = await res.json();
        callback(true, output);
    }
};

export const orderOverwatchSubscription = async (
    geometry: string,
    description: string,
    fromTimestamp: Date,
    toTimestamp: Date | null,
    token: string | null | undefined,
    callback: (orderSubscriptionState: SubscriptionState, reportPath: string | undefined) => void
) => {
    let fromTimeString: string = '';
    let toTimeString: null | string = null;
    if (fromTimestamp) {
        // Ensure right string format
        fromTimeString = fromTimestamp.toJSON();
    }
    if (toTimestamp) {
        toTimeString = toTimestamp.toJSON();
    }
    const body = {
        geometry: geometry,
        start_time: fromTimeString,
        end_time: toTimeString,
        description: description,
    };

    const res = await fetch(`${vake_api_server_url}/report/overwatch/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: getRequestHeaders(token),
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        callback(SubscriptionState.failed, undefined);
    }

    if (res.status === 200) {
        const output = await res.json();
        callback(SubscriptionState.success, output);
    }
};

export const markAsInteresting = async (uuid: string, newMark: string | null, token: string | null | undefined) => {
    const body = {
        mark: newMark,
        report_uuid: uuid,
    };
    const res = await fetch(`${vake_api_server_url}/report/pathfinder/mark_pathfinder_report`, {
        method: 'POST',
        credentials: 'include',
        headers: getRequestHeaders(token),
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error(res.statusText);
    }
};

export const updatePFReportName = async (reportName: string, reportUUID: string, token: string | null | undefined) => {
    const body = {
        report_name: reportName,
        report_uuid: reportUUID,
    };
    const res = await fetch(`${vake_api_server_url}/report/pathfinder/change_report_name`, {
        method: 'POST',
        credentials: 'include',
        headers: getRequestHeaders(token),
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error(res.statusText);
    }
};

export const updatePFDescription = async (
    newReportDescription: string,
    reportUUID: string,
    token: string | null | undefined
) => {
    const body = {
        new_description: newReportDescription,
        report_uuid: reportUUID,
    };
    const res = await fetch(`${vake_api_server_url}/report/pathfinder/change_report_description`, {
        method: 'POST',
        credentials: 'include',
        headers: getRequestHeaders(token),
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error(res.statusText);
    }
};

export const useSendToMattermost = (): UseMutationResult<unknown, Error, string, unknown> => {
    const url = `${vake_api_server_url}/user-feedback/mm`;
    return useMutation<unknown, Error, string>({
        mutationFn: async (text: string) => {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ text: text, channel: 'user-actions' }),
            });
            if (!response.ok) {
                throw new Error('Failed to send message to Mattermost');
            }
        },
    });
};
